import { useFormStore } from '../../lib/store';
import ModuleCard from '../ModuleCard';
import clsx from 'clsx';
import { IconMinus, IconPlus } from '../Icons';
import { formatNtd } from '../ui';

interface MerchandiseModuleProps {
  module: {
    id: string;
    name: string;
    nameEn?: string;
    fields: any[];
    settings: {
      title?: string;
      subtitle?: string;
      headerImageUrl?: string;
      allowMultiple?: boolean;
      items?: {
        id: string;
        name: string;
        nameEn?: string;
        description?: string;
        price: number;
        imageUrl?: string;
        isBundle?: boolean;
        bundleBadge?: string;
        maxQuantity?: number;
      }[];
    };
  };
  event: any;
}

export default function MerchandiseModule({ module }: MerchandiseModuleProps) {
  const { merchandiseItems, setMerchandiseQuantity } = useFormStore();
  const settings = module.settings || {};
  const items = settings.items || [];

  const qty = (itemId: string) => merchandiseItems.find((i) => i.itemId === itemId)?.quantity || 0;
  const step = (itemId: string, delta: number, max = 10) => setMerchandiseQuantity(itemId, Math.max(0, Math.min(max, qty(itemId) + delta)));

  if (items.length === 0) return null;

  return (
    <ModuleCard title={settings.title || module.name} subtitle={settings.subtitle || '可不選，費用會一起計算。'}>
      {settings.headerImageUrl && <img src={settings.headerImageUrl} alt="" className="w-full rounded-tile" />}

      <div className="flex flex-col gap-2.5">
        {items.map((item) => {
          const q = qty(item.id);
          const on = q > 0;
          const max = item.maxQuantity || 10;
          const stepper = settings.allowMultiple && on && max > 1;
          return (
            <div key={item.id} className={clsx(
              'flex flex-col gap-3 p-4 rounded-[16px] border-2 transition-colors',
              on ? 'border-primary bg-primary-soft' : 'border-transparent bg-soft',
            )}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={on} onChange={() => setMerchandiseQuantity(item.id, on ? 0 : 1)} className="w-6 h-6 shrink-0" />
                {item.imageUrl && <img src={item.imageUrl} alt="" className="w-12 h-12 rounded-[10px] object-cover shrink-0" />}
                <div className="flex-1 min-w-0 flex flex-col leading-tight">
                  <span className="text-[15px] font-bold">
                    {item.name}
                    {item.isBundle && item.bundleBadge && (
                      <span className="ml-2 align-middle inline-flex h-5 px-2 rounded-full bg-ink text-white text-[11px] font-bold items-center">{item.bundleBadge}</span>
                    )}
                  </span>
                  {item.description && <span className="text-xs text-muted mt-0.5">{item.description}</span>}
                </div>
                <span className={clsx('tabular text-[16px] font-black shrink-0', on && 'text-primary')}>{formatNtd(item.price)}</span>
              </label>

              {stepper && (
                <div className="flex items-center justify-end gap-3">
                  <span className="text-[13px] text-muted">數量</span>
                  <div className="inline-flex items-center rounded-full bg-white border border-line">
                    <button type="button" onClick={() => step(item.id, -1, max)} aria-label="減少" className="w-10 h-10 inline-flex items-center justify-center text-ink"><IconMinus size={16} /></button>
                    <span className="tabular w-8 text-center font-black">{q}</span>
                    <button type="button" onClick={() => step(item.id, 1, max)} aria-label="增加" disabled={q >= max} className="w-10 h-10 inline-flex items-center justify-center text-ink disabled:opacity-40"><IconPlus size={16} /></button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ModuleCard>
  );
}
